/**
 * Storage that is denied is an ordinary anonymous session (job #768, Sol red-team #762). With site data blocked,
 * merely READING `window.localStorage` throws a SecurityError, and the app touches storage unguarded in dozens of
 * places on the boot path (App.vue, the router, the locale and affiliate readers), so one throw left /guess blank.
 * Rather than guard each call, a denied store is replaced ONCE, first thing, by an in-memory one: everything
 * reads and writes as normal, and nothing outlives the tab. A working store is never touched.
 */
function memoryStorage(): Storage {
  const m = new Map<string, string>()
  const methods: Record<string, unknown> = {
    key: (i: number) => [...m.keys()][i] ?? null,
    getItem: (k: string) => (m.has(String(k)) ? m.get(String(k))! : null),
    setItem: (k: string, v: string) => { m.set(String(k), String(v)) },
    removeItem: (k: string) => { m.delete(String(k)) },
    clear: () => { m.clear() },
  }
  // A Proxy so stored items are own enumerable properties, as on a real Storage: Object.keys / for-in / Object.entries see them.
  return new Proxy({} as Storage, {
    get: (_t, p) => (p === 'length' ? m.size : typeof p === 'string' && p in methods ? methods[p] : typeof p === 'string' && m.has(p) ? m.get(p) : undefined),
    set: (_t, p, v) => { if (typeof p === 'string') m.set(p, String(v)); return true },
    deleteProperty: (_t, p) => { if (typeof p === 'string') m.delete(p); return true },
    has: (_t, p) => typeof p === 'string' && (p in methods || p === 'length' || m.has(p)),
    ownKeys: () => [...m.keys()],
    getOwnPropertyDescriptor: (_t, p) => (typeof p === 'string' && m.has(p) ? { value: m.get(p), writable: true, enumerable: true, configurable: true } : undefined),
  })
}

/** Every key in a Storage, by length/key(i): the one enumeration that is right for real and fallback stores alike. */
export function storageKeys(store: Storage): string[] {
  const out: string[] = []
  for (let i = 0; i < store.length; i++) { const k = store.key(i); if (k !== null) out.push(k) }
  return out
}

/** Swaps in a memory store for each of `names` on `win` that throws on access. Returns the names it replaced. */
export function installSafeStorage(win: Window & typeof globalThis = window, names: Array<'localStorage' | 'sessionStorage'> = ['localStorage', 'sessionStorage']): string[] {
  const replaced: string[] = []
  for (const name of names) {
    try { void win[name].length; continue } catch { /* denied: replace below */ }
    try {
      const mem = memoryStorage()
      Object.defineProperty(win, name, { configurable: true, get: () => mem })
      replaced.push(name)
    } catch { /* cannot redefine: nothing more to do */ }
  }
  return replaced
}
